<div>
	<div id="cesiumContainer" style="z-index: 1; position: absolute; height: 100%; width: 100%;"></div>
</div>

<!-- Add top-center live flight radar loading banner overlay -->
@if (flightRadarGlobeService.isLoading$ | async) {
  <div class="flight-radar-loader-banner">
    <div class="spinner-border text-info spinner-border-sm me-1" role="status"></div>
    <i class="fa-solid fa-plane loader-plane-icon"></i>
    <span class="loader-text">FETCHING REAL-TIME LIVE FLIGHT DATA...</span>
  </div>
}

<!-- Add the notification component -->
<app-notification
  [showNotifications]="showNotifications"
  (showNotificationsChange)="showNotifications = $event"
  [autoOpenOnNewNotifications]="true">
</app-notification>

@if (vehicleMonitor.vehicleViewOn$ | async) {
  @if (vehicleMonitor.latestSnapshot$ | async; as vehicleSnap) {
    @if (vehicleSnap.counts) {
      <div class="vehicle-view-counts" title="Vehicle monitor counts">
        {{ vehicleSnap.counts.total }} cars · auth {{ vehicleSnap.counts.authorised || 0 }} · unauth {{ vehicleSnap.counts.unauthorised || 0 }} · in {{ vehicleSnap.counts.inside }} · out {{ vehicleSnap.counts.outside }}
      </div>
    }
  }
}

<!-- Intelligence Layer Widgets Overlay Stack (Bottom-Right) -->
<div class="globe-bottom-right-stack">
  <!-- Vehicle campus grouping (CDR-style) -->
  @if (vehicleMonitor.vehicleViewOn$ | async) {
    <app-vehicle-campus-legend></app-vehicle-campus-legend>
  }

  <!-- GTD Intelligence Control Panel (Placed directly above CDR) -->
  @if (gtdGlobeService.isLayerVisible$ | async) {
    <app-gtd-control-panel></app-gtd-control-panel>
  }

  <!-- CDR Intelligence Legend (Anchored at the bottom) -->
  @if (cdrGlobeService.isLayerVisible$ | async) {
    <app-cdr-legend></app-cdr-legend>
  }
</div>

<!-- CDR Tower Details Modal -->
<app-cdr-tower-details-modal></app-cdr-tower-details-modal>

<!-- GTD Incident Details Modal -->
<app-gtd-incident-modal></app-gtd-incident-modal>

<!-- CDR Hover Tooltip (Only displayed when details modal is closed) -->
@if (hoverTooltip$ | async; as tooltip) {
  @if (!(cdrGlobeService.selectedTower$ | async)) {
    <div
      class="cdr-globe-tooltip"
      [style.left.px]="getTooltipLeft(tooltip.x)"
      [style.top.px]="getTooltipTop(tooltip.y)"
    >
    <div class="tooltip-header">
      <span class="tooltip-dot" [style.background-color]="getRegionColor(tooltip.tower.region)"></span>
      <span class="tooltip-title">{{ tooltip.tower.cell_id }}</span>
      <span class="tooltip-region">{{ tooltip.tower.region }}</span>
    </div>
    <div class="tooltip-body">
      <div class="tooltip-row">
        <span>Site / Sector:</span>
        <b>{{ tooltip.tower.site_id }} ({{ tooltip.tower.sector }})</b>
      </div>
      <div class="tooltip-row">
        <span>RAT / Status:</span>
        <b class="text-rat">{{ tooltip.tower.supported_rat }} · {{ tooltip.tower.site_status }}</b>
      </div>
      @if (tooltip.stats) {
        <div class="tooltip-divider"></div>
        <div class="tooltip-row">
          <span>Total CDRs:</span>
          <b class="text-cyan">{{ tooltip.stats.totalEvents }}</b>
        </div>
        <div class="tooltip-row">
          <span>Calls / SMS / Data:</span>
          <b>{{ tooltip.stats.voiceCount }} / {{ tooltip.stats.smsCount }} / {{ tooltip.stats.dataCount }}</b>
        </div>
        <div class="tooltip-row">
          <span>Traffic Volume:</span>
          <b class="text-lime">{{ tooltip.stats.totalMB | number:'1.1-2' }} MB</b>
        </div>
      }
    </div>
    <div class="tooltip-hint">Click tower to view full telemetry</div>
  </div>
  }
}

<!-- GTD Incident Hover Tooltip -->
@if (gtdHoverTooltip$ | async; as gtdTooltip) {
  @if (!(gtdGlobeService.selectedIncident$ | async)) {
    <div
      class="gtd-globe-tooltip"
      [style.left.px]="getTooltipLeft(gtdTooltip.x)"
      [style.top.px]="getTooltipTop(gtdTooltip.y)"
    >
      <div class="gtd-tt-header">
        <img [src]="gtdTooltip.incident.iconUrl" [alt]="gtdTooltip.incident.attacktype" class="gtd-tt-badge-img" />
        <span class="gtd-tt-type">{{ gtdTooltip.incident.attacktype }}</span>
        <span class="gtd-tt-state">{{ gtdTooltip.incident.provstate }}</span>
      </div>
      <div class="gtd-tt-body">
        <div class="gtd-tt-row">
          <span>Date:</span>
          <b>{{ gtdTooltip.incident.dateStr }}</b>
        </div>
        <div class="gtd-tt-row">
          <span>Location:</span>
          <b>{{ gtdTooltip.incident.city }}</b>
        </div>
        <div class="gtd-tt-row">
          <span>Perpetrator:</span>
          <b class="text-group-tt">{{ gtdTooltip.incident.gname }}</b>
        </div>
        <div class="gtd-tt-divider"></div>
        <div class="gtd-tt-row">
          <span>Casualties:</span>
          <b class="text-red-tt">
            {{ gtdTooltip.incident.nkill }} killed · {{ gtdTooltip.incident.nwound }} wounded
          </b>
        </div>
        @if (gtdTooltip.incident.target) {
          <div class="gtd-tt-row">
            <span>Target:</span>
            <b class="text-subtle-tt">{{ gtdTooltip.incident.target }}</b>
          </div>
        }
      </div>
      <div class="gtd-tt-hint">Click marker to inspect full intelligence dossier</div>
    </div>
  }
}

@if (vehicleHoverTooltip$ | async; as vehicleTip) {
  <div
    class="vehicle-globe-tooltip"
    [class.authorized]="vehicleTip.vehicle.authorized"
    [class.unauthorized]="!vehicleTip.vehicle.authorized"
    [class.pinned]="!!vehicleTip.pinned"
    [style.left.px]="getTooltipLeft(vehicleTip.x)"
    [style.top.px]="getTooltipTop(vehicleTip.y)"
  >
    <div class="tooltip-header">
      <span class="tooltip-dot" [class.authorized]="vehicleTip.vehicle.authorized" [class.unauthorized]="!vehicleTip.vehicle.authorized"></span>
      <span class="tooltip-title">{{ vehicleTip.vehicle.vehicleNo || vehicleTip.vehicle.trackerId }}</span>
      <span class="tooltip-region" [class.authorized]="vehicleTip.vehicle.authorized" [class.unauthorized]="!vehicleTip.vehicle.authorized">
        {{ vehicleTip.vehicle.authorized ? 'Authorised' : 'Unauthorised' }}
      </span>
      @if (vehicleTip.pinned) {
        <button
          type="button"
          class="tooltip-close"
          title="Close"
          (click)="dismissVehicleHover($event)"
        >×</button>
      }
    </div>
    <div class="tooltip-body">
      <div class="tooltip-row">
        <span>Tracker:</span>
        <b>{{ vehicleTip.vehicle.trackerId }}</b>
      </div>
      @if (vehicleTip.vehicle.registration; as reg) {
        <div class="tooltip-row">
          <span>Vehicle:</span>
          <b>{{ reg.make }} {{ reg.model }}</b>
        </div>
        <div class="tooltip-row">
          <span>Driver:</span>
          <b>{{ reg.driverName || '—' }}</b>
        </div>
        <div class="tooltip-row">
          <span>Type / Colour:</span>
          <b>{{ reg.vehicleType || '—' }} · {{ reg.color || '—' }}</b>
        </div>
      }
      <div class="tooltip-divider"></div>
      <div class="tooltip-row">
        <span>Speed:</span>
        <b>{{ vehicleTip.vehicle.speedKph | number:'1.0-0' }} kph</b>
      </div>
      <div class="tooltip-row">
        <span>Campus:</span>
        <b>{{ vehicleTip.vehicle.campusLabel || vehicleTip.vehicle.homeCampusKey || '—' }}</b>
      </div>
      <div class="tooltip-row">
        <span>Geofence:</span>
        <b>{{ vehicleTip.vehicle.zone }}</b>
      </div>
      @if (vehicleTip.vehicle.roadName) {
        <div class="tooltip-row">
          <span>Road:</span>
          <b>{{ vehicleTip.vehicle.roadName }}</b>
        </div>
      }
    </div>
  </div>
}

